import { LegalOnboardingStatus } from "@/constants/entity-status";
import { AppRoutes } from "@/constants/routes";
import { toaster } from "@/components/ui/toaster";
import {
  getLegalOnboardingRequestDetail,
  UpdateLegalOnboardingDocumentoBody,
  confirmApproveLegalOnboarding,
  createLegalOnboardingAccountInBind,
  PersonType,
  updateLegalOnboardingRequest,
} from "@/services/legal-onboarding.service";
import {
  LegalOnboardingDocumentStatusIds,
  LegalOnboardingStatusIds,
} from "@/constants/enums";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import type { DocDecision } from "../components/doc-list/types";
export type { DocDecision } from "../components/doc-list/types";
import { mergeDocsForSociety } from "./merge-society-documents";
import type { LegalOnboardingDocument } from "./merge-society-documents";
export type {
  LegalOnboardingDocument,
  LegalOnboardingDocumentTipo,
} from "./merge-society-documents";

export interface LegalOnboardingPersonData {
  nombre: string;
  apellido: string;
  mail: string;
  celular: string | null;
  [key: string]: unknown;
}

export interface LegalOnboardingMobileUserData {
  nombreApellido: string;
  dni: string;
}

export type LegalOnboardingDomicilio = {
  calle: string;
  numero: string;
  piso: string;
  localidadId: number;
  provinciaId: number;
  codigoPostal: string;
};

export interface LegalOnboardingDetail {
  id: number;
  cuit: string;
  cuitCuil?: string;
  tipoSociedad?: string;
  razonSocial?: string;
  estado: LegalOnboardingStatus;
  email?: string
  emailUsuarioMobile?: string
  documentos: LegalOnboardingDocument[];
  datosPersona: LegalOnboardingPersonData | null;
  datosUsuario: LegalOnboardingMobileUserData | null;
  esFatca?: boolean;
  esOcde?: boolean;
  actividadPrincipal?: string
  domicilioLegal?: string
  domicilioFiscal?: string
  aceptaTerminosCondiciones?: boolean
  fechaAceptacionTerminos?: string
  ipAceptacionTerminos?: string
}

interface FetchDetailHandlerArgs {
  id: number;
  setLoadingDetail?: (v: boolean) => void;
  withLoading?: boolean;
  setDetail: (v: LegalOnboardingDetail) => void;
  setDecisions: (v: Record<number, DocDecision>) => void;
  setReviewStarted: (v: boolean) => void;
  cancelled?: () => boolean;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

const getInitialDocDecision = (d: any): DocDecision => {
  if (d?.requiereReenvio) return "pending_resend";

  const estadoRaw = d?.estado;
  const estadoIdRaw =
    d?.idEstadoDocumento ??
    d?.idEstado ??
    d?.estadoId ??
    d?.estado?.id ??
    d?.estado?.Id;

  let estadoString: string | null = null;
  if (typeof estadoRaw === "string") {
    estadoString = estadoRaw;
  } else if (typeof d?.estado?.nombre === "string") {
    estadoString = d.estado.nombre;
  }

  if (estadoString === "Aceptado") return "accept";
  if (estadoString === "Rechazado") return "reject";

  const estadoId =
    typeof estadoIdRaw === "number" ? estadoIdRaw : Number(estadoIdRaw);
  if (Number.isFinite(estadoId)) {
    if (estadoId === LegalOnboardingDocumentStatusIds.Aceptado) return "accept";
    if (estadoId === LegalOnboardingDocumentStatusIds.Rechazado)
      return "reject";
  }

  return null;
};

export const fetchAndMapLegalOnboardingDetailHandler = async ({
  id,
  setLoadingDetail,
  withLoading = true,
  setDetail,
  setDecisions,
  setReviewStarted,
  cancelled,
  navigate,
  fetchPermissions,
  routeState,
}: FetchDetailHandlerArgs) => {
  try {
    if (withLoading) setLoadingDetail?.(true);
    const response = await getLegalOnboardingRequestDetail({ id });

    if (cancelled?.()) return;

    const mergedDocs = mergeDocsForSociety(
      response?.tipoSociedad,
      response?.documentos,
    ) as LegalOnboardingDocument[];
    const mergedDetail: LegalOnboardingDetail = {
      ...(response as any),
      cuitCuil: response?.cuit,
      documentos: mergedDocs,
    };

    setDetail(mergedDetail);

    const initialDecisions = (mergedDetail?.documentos ?? []).reduce(
      (acc: Record<number, DocDecision>, d: LegalOnboardingDocument) => {
        const decision = getInitialDocDecision(d);
        if (decision !== null) acc[d.id] = decision;

        return acc;
      },
      {},
    );

    setDecisions(initialDecisions);
    setReviewStarted(
      mergedDetail?.estado === LegalOnboardingStatus.IN_REVIEW ||
        mergedDetail?.estado === LegalOnboardingStatus.REQUIRES_MODIF ||
        mergedDetail?.estado === LegalOnboardingStatus.APPROVED,
    );
  } catch (error) {
    if (cancelled?.()) return;

    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo cargar el detalle."),
      });
    }

    throw error;
  } finally {
    if (!cancelled?.() && withLoading) setLoadingDetail?.(false);
  }
};

interface BindAccountHandlerArgs {
  detail: LegalOnboardingDetail | null;
  domicilio: LegalOnboardingDomicilio;
}

export const bindAccountLegalOnboardingHandler = async ({
  detail,
  domicilio,
}: BindAccountHandlerArgs) => {
  if (!detail) throw new Error("Detalle no disponible");

  return await createLegalOnboardingAccountInBind({
    idSolicitud: detail.id,
    cuit: detail.cuit,
    razonSocial: detail.razonSocial ?? "",
    tipoPersona: PersonType.LegalEntity,
    domicilio,
    cuitCuil: detail.cuitCuil ?? detail.cuit,
  });
};

export type LegalOnboardingDatosCvu = {
  cuit: string;
  alias: string;
  cvu: string;
  idCuenta: number;
  idCvu: number;
};

interface ConfirmApproveHandlerArgs {
  id: number;
  documentos?: LegalOnboardingDocument[];
  decisions?: Record<number, DocDecision>;
  datosCvu: LegalOnboardingDatosCvu;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

const mapDocDecisionsToPayload = (
  documentos: LegalOnboardingDocument[] | undefined,
  decisions: Record<number, DocDecision> | undefined,
): UpdateLegalOnboardingDocumentoBody[] | undefined => {
  if (!documentos?.length || !decisions) return undefined;

  const mapped = documentos
    .map((d) => {
      const decision = decisions[d?.id] ?? null;
      if (decision !== "accept" && decision !== "reject") return null;

      const rawDocId = d?.idDocumento ?? d?.id;
      const idDocumento =
        typeof rawDocId === "number" ? rawDocId : Number(rawDocId);
      if (!Number.isFinite(idDocumento)) return null;

      const estadoId =
        decision === "reject"
          ? LegalOnboardingDocumentStatusIds.Rechazado
          : LegalOnboardingDocumentStatusIds.Aceptado;

      return {
        Id: idDocumento,
        Estado: estadoId,
      } satisfies UpdateLegalOnboardingDocumentoBody;
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  return mapped.length ? mapped : undefined;
};

export const confirmApproveLegalOnboardingHandler = async ({
  id,
  documentos,
  decisions,
  datosCvu,
  navigate,
  fetchPermissions,
  routeState,
}: ConfirmApproveHandlerArgs) => {
  try {
    return await confirmApproveLegalOnboarding(
      id,
      mapDocDecisionsToPayload(documentos, decisions),
      datosCvu,
    );
  } catch (error) {
    await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    throw error;
  }
};

interface StartReviewHandlerArgs {
  id: number;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

export const startReviewLegalOnboardingHandler = async ({
  id,
  navigate,
  fetchPermissions,
  routeState,
}: StartReviewHandlerArgs) => {
  try {
    return await updateLegalOnboardingRequest(id, {
      Id: id,
      IdEstado: LegalOnboardingStatusIds.EnRevision,
    });
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          errorObj,
          "No se pudo iniciar la revisión.",
        ),
      });
    }

    throw error;
  }
};

interface RequireChangesHandlerArgs {
  id: number;
  detail: string;
  documentos?: LegalOnboardingDocument[];
  decisions?: Record<number, DocDecision>;
  navigate: NavigateFunction;
  fetchPermissions?: () => Promise<void>;
  routeState?: any;
}

export const requireChangesLegalOnboardingHandler = async ({
  id,
  detail,
  documentos,
  decisions,
  navigate,
  fetchPermissions,
  routeState,
}: RequireChangesHandlerArgs) => {
  try {
    return await updateLegalOnboardingRequest(id, {
      Id: id,
      IdEstado: LegalOnboardingStatusIds.RequiereCambios,
      Observacion: detail,
      Documentos: mapDocDecisionsToPayload(documentos, decisions),
    });
  } catch (error) {
    const errorObj = error as AxiosError;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.LEGAL_ONBOARDING,
      routeState,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(errorObj, "No se pudo requerir cambios."),
      });
    }

    throw error;
  }
};
